/**
 * Mini TV controller hook: owns video URL state from effectiveEventResults.vod (GET /live or GET results).
 * Phase (presentation | execution | ended) comes from run store; hook exposes reset().
 */
import React from 'react';
import { useMiniTvRun } from '../stores';

const FOOTBALL_SPORT_ID = 50;
const VOD_EXPIRY_MARGIN_MS = 5 * 60 * 1000;

function isVodExpiredOrExpiring(vod: Record<string, unknown> | null | undefined): boolean {
  if (!vod || vod.exp == null) return false;
  const exp = Number(vod.exp);
  const expMs = exp < 1e12 ? exp * 1000 : exp;
  if (!Number.isFinite(expMs)) return false;
  return expMs - Date.now() < VOD_EXPIRY_MARGIN_MS;
}

export interface UseMiniTvControllerParams {
  scheduleId: string | number | null | undefined;
  eventId: string | number | null | undefined;
  sportId: number | null;
  ctxKey: string;
  isRacingSchedule: boolean;
  isBaseballSchedule: boolean;
  useBatchVod: boolean;
  videoIdSequence: string[];
  miniTvVideoId: string | null;
  effectiveEventResults: Record<string, unknown> | null | undefined;
  /** When racing VOD is expired, call to refetch GET /live so effectiveEventResults gets fresh vod. */
  refetchOngoing?: () => void;
}

export type MiniTvPhase = 'presentation' | 'execution' | 'ended';

export interface UseMiniTvControllerReturn {
  phase: MiniTvPhase;
  videoUrl: string | null;
  videoUrls: string[] | null;
  avh: unknown;
  videoReady: boolean;
  setVideoReady: (value: boolean) => void;
  reset: () => void;
}

export function useMiniTvController(params: UseMiniTvControllerParams): UseMiniTvControllerReturn {
  const {
    scheduleId,
    sportId,
    ctxKey,
    isRacingSchedule,
    isBaseballSchedule,
    useBatchVod,
    videoIdSequence,
    miniTvVideoId,
    effectiveEventResults,
    refetchOngoing,
  } = params;

  const { getRun, clearRun } = useMiniTvRun();
  const run = scheduleId != null ? getRun(scheduleId) : null;
  const phase: MiniTvPhase = run?.phase === 'ended' ? 'ended' : run?.phase === 'execution' ? 'execution' : 'presentation';

  const [videoUrl, setVideoUrl] = React.useState<string | null>(null);
  const [videoUrls, setVideoUrls] = React.useState<string[] | null>(null);
  const [avh, setAvh] = React.useState<unknown>(null);
  const [videoReady, setVideoReady] = React.useState(false);

  const prevScheduleRef = React.useRef(scheduleId);
  if (prevScheduleRef.current !== scheduleId) {
    prevScheduleRef.current = scheduleId;
    setVideoUrl(null);
    setVideoUrls(null);
    setAvh(null);
    setVideoReady(false);
  }

  React.useEffect(() => {
    setVideoReady(false);
  }, [ctxKey]);

  React.useEffect(() => {
    const cleanup = () => {};

    const vod = effectiveEventResults?.vod as Record<string, unknown> | undefined;

    if (isRacingSchedule) {
      const hlsUrl = vod?.hls_url != null && String(vod.hls_url).trim() !== '' ? String(vod.hls_url).trim() : null;
      if (!hlsUrl) {
        setVideoUrl(null);
        setVideoUrls(null);
        setAvh(null);
        return cleanup;
      }
      if (isVodExpiredOrExpiring(vod ?? null)) {
        refetchOngoing?.();
      }
      setVideoUrl(hlsUrl);
      setVideoUrls(null);
      setAvh(vod?.avh ?? null);
      return cleanup;
    }

    if (sportId === FOOTBALL_SPORT_ID) {
      const raw = Array.isArray(vod?.urls) ? vod.urls : [];
      const urls = raw.map((u: unknown) => (u != null ? String(u).trim() : '')).filter((u) => u.length > 0);
      if (urls.length > 0) {
        setVideoUrls(urls);
        setVideoUrl(urls[0]);
        setAvh(vod?.avh ?? null);
        return cleanup;
      }
      setVideoUrl(null);
      setVideoUrls(null);
      setAvh(null);
      return cleanup;
    }

    if (isBaseballSchedule) {
      const urls = Array.isArray(vod?.urls) ? (vod.urls as unknown[]).map(String).filter((s) => s.trim().length > 0) : [];
      if (urls.length > 0) {
        setVideoUrls(urls);
        setVideoUrl(urls[0]);
        setAvh(vod?.avh ?? null);
        return cleanup;
      }
      setVideoUrl(null);
      setVideoUrls(null);
      setAvh(null);
      return cleanup;
    }

    if (useBatchVod && videoIdSequence.length > 0) {
      setVideoUrl(null);
      setVideoUrls(null);
      setAvh(null);
      return cleanup;
    }

    setVideoUrl(null);
    setVideoUrls(null);
    setAvh(null);
    return cleanup;
  }, [
    ctxKey,
    isRacingSchedule,
    isBaseballSchedule,
    useBatchVod,
    miniTvVideoId,
    sportId,
    effectiveEventResults?.vod,
    refetchOngoing,
    useBatchVod ? videoIdSequence.join(',') : '',
  ]);

  const reset = React.useCallback(() => {
    if (scheduleId != null) clearRun(scheduleId);
    setVideoReady(false);
    setVideoUrl(null);
    setVideoUrls(null);
    setAvh(null);
  }, [scheduleId, clearRun]);

  return {
    phase,
    videoUrl,
    videoUrls,
    avh,
    videoReady,
    setVideoReady,
    reset,
  };
}
